/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { isAxiosError } from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import type {
  HomepageBlockResponse,
  Platform,
} from '../models/homepageBlockTypes';
import { HomepageRequestSchema } from '../schema/homepageRequestSchema';
import { HomepageApiResponseSchema } from '../schema/homepageResponseSchema';

declare const __DEV__: boolean;

export interface ServiceLogger {
  info: (message: string) => void;
  error: (message: string, meta?: Record<string, unknown>) => void;
  log: (message: string) => void;
  /** Full request/response dumps. Loggers without it get them on `info`. */
  debug?: (message: string) => void;
  /** Lets the service skip serialising dumps the logger would drop anyway. */
  isDebugEnabled?: () => boolean;
}

const defaultLogger: ServiceLogger = {
  info: (message) => console.log(message),
  error: (message, meta) => console.error(message, meta ?? ''),
  log: (message) => console.log(message),
  debug: (message) => console.log(message),
};

export type CosmosApiPlatform =
  | 'mobile'
  | 'web'
  | 'desktop'
  | 'ios'
  | 'android';

const formatDebugPayload = (payload: unknown) =>
  JSON.stringify(payload, null, 2);

const shouldLogCosmosDebug = () => {
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    return true;
  }

  const webEnv = process.env.NEXT_PUBLIC_ENV?.toLowerCase();

  // prevent web client logs in prd
  if (!webEnv || typeof window !== 'undefined') {
    return false;
  }

  // Keep Cosmos logs enabled on deployed non-prod and prd home-web builds, where __DEV__ is false.
  return true;
};

const mapPlatformToApiPlatform = (platform: Platform): CosmosApiPlatform => {
  switch (platform) {
    case 'ios':
      return 'ios';
    case 'android':
      return 'android';
    case 'mobile':
      return 'mobile';
    case 'web-mobile':
      return 'web';
    case 'web':
      return 'web';
    default:
      return 'web';
  }
};

export class HomepageService {
  private client: ApiClient;
  private logger: ServiceLogger;
  private apiKey?: string;

  constructor(
    baseUrl?: string,
    apiKey?: string,
    timeout: number = 30000,
    logger?: ServiceLogger,
  ) {
    this.logger = logger ?? defaultLogger;
    this.apiKey = apiKey;
    this.client = CoreNetworking({
      baseUrl,
      timeout,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
    });

    /**
     * Request logging & validation
     */
    this.client.interceptors.request.use(
      (config) => {
        const rawHeaders = config.headers;
        const contentType =
          (typeof rawHeaders?.get === 'function'
            ? rawHeaders.get('Content-Type')
            : rawHeaders?.['Content-Type'] || rawHeaders?.['content-type']) ||
          'application/json';
        const apiKeyHeader =
          (typeof rawHeaders?.get === 'function'
            ? rawHeaders.get('X-API-Key')
            : rawHeaders?.['X-API-Key'] || rawHeaders?.['x-api-key']) ||
          this.apiKey ||
          '';

        const validationResult = HomepageRequestSchema.safeParse({
          headers: {
            'Content-Type': contentType,
            'X-API-Key': apiKeyHeader,
          },
          params: config.params,
          body: config.data,
        });

        if (!validationResult.success) {
          const { currency, geoId, locale } =
            (config.params as Record<string, unknown> | undefined) ?? {};
          this.logger.error(
            `❌ [Server side] [CosmosApi] Request validation failed, currency - ${currency}, geoId - ${geoId}, locale - ${locale}:`,
            {
              summary: validationResult.error.issues.map(
                (i) => `${i.path.join('.') || 'root'}: ${i.message}`,
              ),
              issues: validationResult.error.issues,
              errors: validationResult.error.format(),
            },
          );
          return Promise.reject(validationResult.error);
        }

        if (this.shouldLogDebug()) {
          this.logDebug(
            `🚀 [Server side] [CosmosApi] REQUEST:\n${formatDebugPayload({
              method: config.method?.toUpperCase(),
              baseURL: config.baseURL,
              url: config.url,
              fullUrl: `${config.baseURL}${config.url}`,
              headers: config.headers,
              params: config.params,
            })}`,
          );
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    /**
     * Response logging & validation
     */
    this.client.interceptors.response.use(
      (response) => {
        const validationResult = HomepageApiResponseSchema.safeParse(
          response.data,
        );

        if (!validationResult.success) {
          const { currency, geoId, locale } =
            (response.config?.params as Record<string, unknown> | undefined) ??
            {};
          this.logger.error(
            `❌ [Server side] [CosmosApi] Response validation failed, currency - ${currency}, geoId - ${geoId}, locale - ${locale}:`,
            {
              summary: validationResult.error.issues.map(
                (i) => `${i.path.join('.') || 'root'}: ${i.message}`,
              ),
              issues: validationResult.error.issues,
              errors: validationResult.error.format(),
            },
          );
          return Promise.reject(validationResult.error);
        }

        if (this.shouldLogDebug()) {
          this.logDebug(
            `✅ [Server side] [CosmosApi] RESPONSE (raw):\n${formatDebugPayload(
              {
                status: response.status,
                statusText: response.statusText,
                data: response.data,
              },
            )}`,
          );
        }
        return response;
      },
      (error) => {
        if (shouldLogCosmosDebug()) {
          this.logger.error('❌ [Server side] [CosmosApi] ERROR:', {
            status: error.response?.status,
            message: error.message,
          });
        }
        return Promise.reject(error);
      },
    );
  }

  /** Payload dumps are large: only build them when the logger will write them. */
  private shouldLogDebug() {
    return shouldLogCosmosDebug() && this.logger.isDebugEnabled?.() !== false;
  }

  private logDebug(message: string) {
    if (this.logger.debug) {
      this.logger.debug(message);
    } else {
      this.logger.info(message);
    }
  }

  async getHomePage(
    platform: Platform,
    feature: string,
    lob: string,
    locale: string,
    geoId: string,
    currency?: string,
  ): Promise<HomepageBlockResponse> {
    const apiPlatform = mapPlatformToApiPlatform(platform);
    const params: Record<string, string> = {
      platform: apiPlatform,
      feature: feature,
      lob: lob,
      locale: locale,
      geoId: geoId,
    };
    if (currency) {
      params.currency = currency;
    }

    try {
      const response = await this.client.post<HomepageBlockResponse>(
        '/v1/content',
        undefined,
        { params },
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const status = error.response?.status ?? 'Network Error';
        const message = error.response?.data?.message ?? error.message;
        throw {
          code: String(status),
          message: message,
        };
      }
      throw error;
    }
  }
}
